/**
 * Hooks lookups V2 — listes pour les dropdowns du formulaire de création
 * apprenant : Formations, Formateurs, Entreprises, Leads.
 *
 * Tous tapent les routes V1 existantes (cache 5min côté API).
 * Pas de fallback mock — si la liste est vide, le dropdown sera vide.
 */
import { useQuery } from '@tanstack/react-query';
import { api } from '../../../lib/api.js';

export interface LookupItem {
  id: string;
  label: string;
  /** Détail secondaire affiché en small */
  sublabel?: string;
  /** Données complètes (pour preview après sélection) */
  raw?: unknown;
}

/**
 * ⭐⭐⭐ LE CATALOGUE NATIF — 09/10/2026.
 *
 * `useFormationsList` lit `/api/v1/formations`, le cache de l'ANCIENNE base.
 * La consigne est explicite : l'archive ne détermine pas la FORMATION d'un
 * apprenant — et son identifiant n'existe nulle part dans Andalous, donc le
 * choix se perdait entre l'écran et le dossier.
 *
 * ⚠️ MÊME DÉFAUT ICI QUE DANS L'AUTRE ÉCRAN. Les deux applications sont
 *    servies en production (`cockpit_web_prod` et `cockpit_web_v2_prod`) :
 *    n'en corriger qu'une laisserait la panne attendre dans l'autre.
 *
 *        ⛔ DEUX ÉCRANS POUR UN MÊME GESTE = UNE PANNE QUI ATTEND.
 */
interface CatalogueFormation {
  id: string;
  code: string;
  intitule: string;
  categorie: string;
  actif: boolean;
  formules: Array<{ id: string; libelle: string; duree: number; prix: number }>;
}

export function useOffresCatalogue(): { items: LookupItem[]; isLoading: boolean } {
  const { data, isLoading } = useQuery({
    queryKey: ['catalogue', 'offres-natives'],
    queryFn: () => api.get<CatalogueFormation[]>('/api/v1/catalogue'),
    staleTime: 5 * 60_000,
  });
  const items: LookupItem[] = (data ?? [])
    .filter((f) => f.actif)
    .map((f) => ({
      id: f.id,
      label: f.intitule,
      sublabel: [
        f.categorie,
        f.formules.length === 1
          ? `${f.formules[0]!.duree}h · ${f.formules[0]!.prix.toLocaleString('fr-FR')}€`
          : `${f.formules.length} formules`,
      ]
        .filter(Boolean)
        .join(' · '),
      raw: f,
    }));
  return { items, isLoading };
}

interface FormationsResponse {
  formations: Array<{
    id: string;
    nom: string;
    nomCommercial: string;
    codeInterne: string;
    tarif: number;
    dureeHeures: number;
    typeFormation: string;
    certification: string;
  }>;
}

export function useFormationsList(): { items: LookupItem[]; isLoading: boolean } {
  const { data, isLoading } = useQuery({
    queryKey: ['v2', 'lookups', 'formations'],
    queryFn: () => api.get<FormationsResponse>('/api/v1/formations'),
    staleTime: 5 * 60_000,
  });
  const items: LookupItem[] = (data?.formations ?? []).map((f) => ({
    id: f.id,
    label: f.nomCommercial || f.nom,
    sublabel: `${f.codeInterne || ''} · ${f.dureeHeures}h · ${f.tarif.toLocaleString('fr-FR')}€${
      f.certification ? ' · ' + f.certification : ''
    }`,
    raw: f,
  }));
  return { items, isLoading };
}

interface FormateursResponse {
  formateurs: Array<{
    id: string;
    nom: string;
    prenom: string;
    email: string;
    specialites: string[];
    statut: string;
  }>;
}

export function useFormateursList(): { items: LookupItem[]; isLoading: boolean } {
  const { data, isLoading } = useQuery({
    queryKey: ['v2', 'lookups', 'formateurs'],
    queryFn: () => api.get<FormateursResponse>('/api/v1/formateurs'),
    staleTime: 5 * 60_000,
  });
  const items: LookupItem[] = (data?.formateurs ?? [])
    .filter((f) => f.statut !== 'Inactif')
    .map((f) => ({
      id: f.id,
      label: `${f.prenom} ${f.nom}`,
      sublabel: [f.email, (f.specialites || []).join(', ')].filter(Boolean).join(' · '),
      raw: f,
    }));
  return { items, isLoading };
}

interface EntreprisesResponse {
  entreprises: Array<{ id: string; nom: string }>;
}

export function useEntreprisesList(): { items: LookupItem[]; isLoading: boolean } {
  const { data, isLoading } = useQuery({
    queryKey: ['v2', 'lookups', 'entreprises'],
    queryFn: () => api.get<EntreprisesResponse>('/api/v1/entreprises'),
    staleTime: 5 * 60_000,
  });
  const items: LookupItem[] = (data?.entreprises ?? []).map((e) => ({
    id: e.id,
    label: e.nom,
    raw: e,
  }));
  return { items, isLoading };
}

interface LeadsResponse {
  leads: Array<{
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    source: string;
    status: string;
    formationInteresse: string;
    dateContact: string;
    notes: string;
  }>;
}

export function useLeadsList(): { items: LookupItem[]; isLoading: boolean } {
  const { data, isLoading } = useQuery({
    queryKey: ['v2', 'lookups', 'leads'],
    queryFn: () => api.get<LeadsResponse>('/api/v1/leads'),
    staleTime: 2 * 60_000,
  });
  const items: LookupItem[] = (data?.leads ?? []).map((l) => ({
    id: l.id,
    label: `${l.firstName} ${l.lastName}`.trim() || l.email || l.id,
    sublabel: [l.email, l.source, l.status, l.formationInteresse].filter(Boolean).join(' · '),
    raw: l,
  }));
  return { items, isLoading };
}
